"use client";

import React, { memo, useCallback, useState } from "react";
import { alpha, useTheme } from "@mui/material/styles";
import { Box } from "@mui/material";
import DashboardLayout from "@/app/components/DashboardLayout";
import LocationUpdater from "@/app/components/LocationUpdater";
import SessionProviders from "@/app/components/SessionProvider";
import ErrorBoundary from "@/app/components/ErrorBoundary";
import { TimezoneProvider } from "@/app/utils/TimezoneContext";
import VercelAnalytics from "@/lib/analytics/VercelAnalytics";
import TopLoadingProgress from "@/app/components/notifier/TopLoadingProgress";

const MemoizedDashboardLayout = memo(DashboardLayout);
const MemoizedTimezoneProvider = memo(TimezoneProvider);
const SHOW_HANDOFF_LOADING_OVERLAY = false;
const BLOCK_INTERACTION_DURING_HANDOFF = true;

export default function SharedDashboardWrapper({
  children,
  session,
  initialShowEnrichmentLoading = false,
}) {
  const theme = useTheme();
  const [handoffActive, setHandoffActive] = useState(Boolean(session));
  const handleHandoffChange = useCallback((active) => {
    setHandoffActive(active);
  }, []);

  return (
    <SessionProviders session={session}>
      <MemoizedTimezoneProvider>
        <ErrorBoundary>
          <LocationUpdater />
          <VercelAnalytics />
          {BLOCK_INTERACTION_DURING_HANDOFF && handoffActive && (
            <>
              <Box
                aria-hidden="true"
                sx={{
                  position: "fixed",
                  inset: 0,
                  zIndex: 1299,
                  pointerEvents: "auto",
                  backgroundColor: SHOW_HANDOFF_LOADING_OVERLAY
                    ? theme.palette.mode === "dark"
                      ? alpha(theme.palette.background.default, 0.42)
                      : alpha(theme.palette.background.default, 0.32)
                    : "transparent",
                  ...(SHOW_HANDOFF_LOADING_OVERLAY && {
                    backdropFilter: "blur(2px)",
                    WebkitBackdropFilter: "blur(2px)",
                  }),
                }}
              />
              {SHOW_HANDOFF_LOADING_OVERLAY && <TopLoadingProgress />}
            </>
          )}
          <MemoizedDashboardLayout
            onVerificationHandoffChange={handleHandoffChange}
            initialShowEnrichmentLoading={initialShowEnrichmentLoading}
          >
            {children}
          </MemoizedDashboardLayout>
        </ErrorBoundary>
      </MemoizedTimezoneProvider>
    </SessionProviders>
  );
}
